import * as React from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { ArrowUpDown, CalendarClock, Check, ChevronDown, ChevronUp, Plus } from "lucide-react";
import { apiFetch, APIError } from "@/lib/api-client";
import { useAuth } from "@/features/auth/auth-context";
import { toast } from "@/components/ui/toaster";
import { HeatBadge } from "@/components/ui/heat-badge";
import type { ConfirmedLevel } from "@/lib/stufen-labels";
import type { DashboardStatusSlim, Room, RoomListResponse, RoomWriteBody } from "./types";

const STALE_THRESHOLD_MS = 24 * 60 * 60 * 1000;

// B3: Bridge API-String-Enum → DB-Wert 0-3 (HeatBadge single-source).
const LEVEL_SLUG_TO_CONFIRMED: Record<string, 0 | 1 | 2 | 3> = {
  normal: 0,
  soll: 1,
  muss: 2,
  kritisch: 3,
};

const listDateFormatter = new Intl.DateTimeFormat("de-DE", {
  dateStyle: "short",
  timeStyle: "short",
});

/** Zusammengeführter live-stand pro raum (aus /v1/dashboard/status). */
interface RoomLiveStatus {
  badge: ConfirmedLevel | null;
  indoorTemp?: number;
  eventAt?: string;
  isProvisional: boolean;
  isStale: boolean;
}

const roomSchema = z.object({
  name: z.string().min(1, "Name pflicht").max(100, "Max 100 Zeichen"),
  etage: z.string().max(120, "Max 120 Zeichen").optional(),
  kapazitaet: z.coerce
    .number()
    .int("Ganze Zahl")
    .min(1, "Mindestens 1")
    .max(9999, "Max 9999")
    .optional()
    .or(z.literal("").transform(() => undefined)),
  notiz: z.string().max(2000, "Max 2000 Zeichen").optional(),
});

type RoomFormData = z.infer<typeof roomSchema>;

export function RoomsListRoute() {
  const auth = useAuth();
  const qc = useQueryClient();
  const canWrite = auth.user?.role === "admin" || auth.user?.role === "leitung";
  const [showCreate, setShowCreate] = React.useState(false);
  const [reorderMode, setReorderMode] = React.useState(false);

  const rooms = useQuery({
    queryKey: ["rooms", "list"],
    queryFn: () => apiFetch<RoomListResponse>("/v1/rooms"),
  });

  // Live-stand pro raum (stufe + letzte messung). Best-effort: schlägt der
  // status-call fehl, rendert die liste ohne badges statt zu blockieren.
  const status = useQuery({
    queryKey: ["dashboard", "status"],
    queryFn: () => apiFetch<DashboardStatusSlim>("/v1/dashboard/status"),
    staleTime: 5 * 60 * 1000,
  });

  const liveByRoom = React.useMemo(() => {
    const map = new Map<string, RoomLiveStatus>();
    const now = Date.now();
    for (const r of status.data?.rooms ?? []) {
      const le = r.last_entry;
      map.set(r.room_id, {
        badge: le ? (LEVEL_SLUG_TO_CONFIRMED[le.confirmed_level] ?? null) : null,
        indoorTemp: le?.indoor_temp_c,
        eventAt: le?.event_at,
        isProvisional: le?.is_provisional ?? false,
        isStale: le
          ? Number.isFinite(Date.parse(le.event_at)) &&
            now - Date.parse(le.event_at) > STALE_THRESHOLD_MS
          : false,
      });
    }
    return map;
  }, [status.data]);

  const roomCount = rooms.data?.rooms.length ?? 0;

  // Reorder beenden: dashboard cache invalidieren, damit die (ggf. neue)
  // reihenfolge auch dort sofort greift (liest ebenfalls ORDER BY sort_order).
  const exitReorder = () => {
    setReorderMode(false);
    void qc.invalidateQueries({ queryKey: ["dashboard"] });
  };

  return (
    <section className="space-y-6">
      <header className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="text-3xl font-semibold">Räume</h1>
        {canWrite && !reorderMode && (
          <div className="flex flex-wrap items-center gap-2">
            {roomCount > 1 && (
              <button
                type="button"
                onClick={() => {
                  setShowCreate(false);
                  setReorderMode(true);
                }}
                className="inline-flex h-12 items-center gap-2 rounded-md border border-[color:var(--color-border)] px-4 font-medium hover:bg-[color:var(--color-surface-sunken)]"
              >
                <ArrowUpDown aria-hidden="true" className="size-4" />
                Reihenfolge ändern
              </button>
            )}
            <button
              type="button"
              onClick={() => setShowCreate((v) => !v)}
              className="inline-flex h-12 items-center gap-2 rounded-md bg-[color:var(--color-brand-primary-strong)] px-4 font-semibold text-white"
            >
              <Plus aria-hidden="true" className="size-4" />
              {showCreate ? "Abbrechen" : "Raum anlegen"}
            </button>
          </div>
        )}
        {canWrite && reorderMode && (
          <button
            type="button"
            onClick={exitReorder}
            className="inline-flex h-12 items-center gap-2 rounded-md bg-[color:var(--color-brand-primary-strong)] px-4 font-semibold text-white"
          >
            <Check aria-hidden="true" className="size-4" />
            Fertig
          </button>
        )}
      </header>

      {showCreate && !reorderMode && (
        <RoomCreateForm onDone={() => setShowCreate(false)} />
      )}

      {rooms.isPending && <p className="text-sm text-neutral-500">Lade Räume …</p>}
      {rooms.isError && (
        <p className="rounded-md bg-[color:var(--color-heat-4-soft)] px-3 py-2 text-sm">
          Räume konnten nicht geladen werden.
        </p>
      )}
      {rooms.data &&
        (reorderMode ? (
          <RoomsReorderList rooms={rooms.data.rooms} onForceExit={() => setReorderMode(false)} />
        ) : (
          <RoomsList rooms={rooms.data.rooms} liveByRoom={liveByRoom} canWrite={canWrite} />
        ))}
    </section>
  );
}

function RoomsList({
  rooms,
  liveByRoom,
  canWrite,
}: {
  rooms: Room[];
  liveByRoom: Map<string, RoomLiveStatus>;
  canWrite: boolean;
}) {
  if (rooms.length === 0) {
    return (
      <p className="rounded-md bg-[color:var(--color-surface-elevated)] p-6 text-center text-sm text-neutral-600">
        Noch keine Räume vorhanden. {canWrite && "Mit „Raum anlegen“ starten."}
      </p>
    );
  }
  return (
    <ul className="grid grid-cols-1 gap-3 sm:grid-cols-2">
      {rooms.map((r) => {
        const live = liveByRoom.get(r.id);
        return (
          <li key={r.id}>
            <Link
              to={`/raeume/${r.id}`}
              className="flex h-full flex-col rounded-lg border border-[color:var(--color-border)] bg-white p-4 transition-colors hover:border-[color:var(--color-border-strong)]"
            >
              <header className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <h2 className="text-lg font-semibold">{r.name}</h2>
                  {(r.etage || r.kapazitaet) && (
                    <p className="text-xs text-neutral-500">
                      {[r.etage, r.kapazitaet && `${r.kapazitaet} Plätze`]
                        .filter(Boolean)
                        .join(" · ")}
                    </p>
                  )}
                </div>
                {live?.badge && (
                  <HeatBadge level={live.badge} temp={live.indoorTemp} compact />
                )}
              </header>

              <div className="mt-2 flex flex-wrap items-center gap-2">
                {live?.eventAt ? (
                  <p className="inline-flex items-center gap-1 text-xs text-[color:var(--color-text-secondary)]">
                    <CalendarClock aria-hidden="true" strokeWidth={2} className="size-3.5" />
                    {listDateFormatter.format(new Date(live.eventAt))} Uhr
                  </p>
                ) : (
                  <p className="text-xs text-[color:var(--color-text-muted)]">
                    Noch keine Messung
                  </p>
                )}
                {live?.isProvisional && (
                  <span className="rounded bg-[color:var(--color-heat-2-soft)] px-1.5 py-0.5 text-[0.7rem] text-[color:var(--color-text-primary)]">
                    vorläufig
                  </span>
                )}
                {live?.isStale && (
                  <span className="rounded bg-[color:var(--color-surface-sunken)] px-1.5 py-0.5 text-[0.7rem] text-[color:var(--color-text-secondary)]">
                    &gt; 24 h
                  </span>
                )}
              </div>

              {r.notiz && (
                <p className="mt-2 text-xs italic text-neutral-600">„{r.notiz}“</p>
              )}
            </Link>
          </li>
        );
      })}
    </ul>
  );
}

/**
 * moveInOrder — reine swap-funktion hinter den ⬆️/⬇️-buttons. Liefert eine
 * NEUE liste mit `index` ↔ `index+dir` getauscht, oder `null` wenn das ziel
 * außerhalb liegt (= no-op, der button ist an der grenze ohnehin disabled).
 * Mutiert die eingabe nicht. Exportiert als test-oberfläche (unit-getestet).
 */
export function moveInOrder<T>(order: readonly T[], index: number, dir: -1 | 1): T[] | null {
  const target = index + dir;
  if (target < 0 || target >= order.length) return null;
  const next = order.slice();
  const a = next[index];
  const b = next[target];
  if (a === undefined || b === undefined) return null;
  next[index] = b;
  next[target] = a;
  return next;
}

/**
 * RoomsReorderList — einspaltige reorder-ansicht (nur im reorder-modus, nur
 * canWrite). ⬆️/⬇️ verschieben einen raum um eine position; jeder klick
 * persistiert die VOLLSTÄNDIGE neue reihenfolge via POST /v1/rooms/reorder
 * (server vergibt sort_order = index, atomar). Optimistisch: lokaler order-state
 * für sofortiges feedback; bei fehler revert auf serverstand.
 *
 * Einspaltig statt grid, damit ⬆️/⬇️ eindeutig „visuell oben/unten" bedeuten
 * (im 2-spalten-grid wäre die nachbar-position mehrdeutig). Kein drag&drop —
 * buttons sind touch-/scroll-sicher.
 *
 * Race-schutz: solange ein reorder-POST läuft, sind die pfeile disabled →
 * nie zwei absolute-state-POSTs gleichzeitig (deren server-reihenfolge sonst
 * undefiniert wäre). Bei wenigen räumen ist der POST sehr kurz.
 */
function RoomsReorderList({
  rooms,
  onForceExit,
}: {
  rooms: Room[];
  onForceExit: () => void;
}) {
  const qc = useQueryClient();
  // Seed einmalig beim mount (mode-toggle re-mountet die komponente). Ein
  // hintergrund-refetch von ["rooms","list"] überschreibt die lokale order
  // also nicht mitten im sortieren.
  const [order, setOrder] = React.useState<Room[]>(() => rooms);

  const reorder = useMutation({
    mutationFn: (orderedIds: string[]) =>
      apiFetch<RoomListResponse>("/v1/rooms/reorder", {
        method: "POST",
        body: { ordered_ids: orderedIds },
      }),
    onSuccess: (data) => {
      // Server liefert den frischen geordneten stand → cache direkt setzen
      // (kein invalidate-flicker), damit die normale liste nach „Fertig" passt.
      qc.setQueryData(["rooms", "list"], data);
    },
    onError: (err) => {
      if (err instanceof APIError && err.code === "room_set_mismatch") {
        // Stale client: ein raum wurde anderswo angelegt/gelöscht. Nicht still
        // teilweise sortieren — neu laden + modus verlassen.
        toast("Die Raumliste hat sich geändert — bitte erneut öffnen", "warning");
        void qc.invalidateQueries({ queryKey: ["rooms"] });
        onForceExit();
        return;
      }
      toast("Reihenfolge konnte nicht gespeichert werden", "danger");
      setOrder(rooms); // revert auf serverstand
    },
  });

  const move = (index: number, dir: -1 | 1) => {
    const next = moveInOrder(order, index, dir);
    if (!next) return; // ziel out-of-bounds (pfeil ist dann ohnehin disabled)
    setOrder(next);
    reorder.mutate(next.map((r) => r.id));
  };

  return (
    <div>
      <p className="mb-3 text-sm text-[color:var(--color-text-secondary)]">
        Mit den Pfeilen ordnen — die Reihenfolge gilt überall (Sammel-Modus,
        Dashboard, Protokoll-PDF).
      </p>
      <ul className="flex flex-col gap-2">
        {order.map((r, i) => (
          <li
            key={r.id}
            className="flex items-center gap-3 rounded-lg border border-[color:var(--color-border)] bg-white p-3"
          >
            <span className="w-6 shrink-0 text-center text-sm font-semibold text-[color:var(--color-text-muted)]">
              {i + 1}
            </span>
            <div className="min-w-0 flex-1">
              <p className="truncate font-semibold">{r.name}</p>
              {(r.etage || r.kapazitaet) && (
                <p className="truncate text-xs text-neutral-500">
                  {[r.etage, r.kapazitaet && `${r.kapazitaet} Plätze`]
                    .filter(Boolean)
                    .join(" · ")}
                </p>
              )}
            </div>
            <div className="flex shrink-0 items-center gap-1">
              <button
                type="button"
                onClick={() => move(i, -1)}
                disabled={i === 0 || reorder.isPending}
                aria-label={`„${r.name}“ nach oben`}
                className="inline-flex size-12 items-center justify-center rounded-md border border-[color:var(--color-border)] hover:bg-[color:var(--color-surface-sunken)] disabled:opacity-30 disabled:hover:bg-transparent"
              >
                <ChevronUp aria-hidden="true" className="size-5" />
              </button>
              <button
                type="button"
                onClick={() => move(i, 1)}
                disabled={i === order.length - 1 || reorder.isPending}
                aria-label={`„${r.name}“ nach unten`}
                className="inline-flex size-12 items-center justify-center rounded-md border border-[color:var(--color-border)] hover:bg-[color:var(--color-surface-sunken)] disabled:opacity-30 disabled:hover:bg-transparent"
              >
                <ChevronDown aria-hidden="true" className="size-5" />
              </button>
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}

function RoomCreateForm({ onDone }: { onDone: () => void }) {
  const qc = useQueryClient();
  const form = useForm<RoomFormData>({
    resolver: zodResolver(roomSchema),
    defaultValues: { name: "", etage: "", notiz: "" },
  });
  const create = useMutation({
    mutationFn: async (body: RoomWriteBody) =>
      apiFetch<Room>("/v1/rooms", {
        method: "POST",
        body,
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["rooms"] });
      void qc.invalidateQueries({ queryKey: ["dashboard"] });
      toast("Raum angelegt", "success");
      onDone();
    },
    onError: (err) => {
      if (err instanceof APIError && err.code === "name_already_exists") {
        toast("Ein Raum mit diesem Namen existiert bereits", "warning");
      } else {
        toast("Raum konnte nicht angelegt werden", "danger");
      }
    },
  });

  const submit = (data: RoomFormData) => {
    const body: RoomWriteBody = {
      name: data.name.trim(),
    };
    if (data.etage && data.etage.trim()) body.etage = data.etage.trim();
    if (typeof data.kapazitaet === "number") body.kapazitaet = data.kapazitaet;
    if (data.notiz && data.notiz.trim()) body.notiz = data.notiz.trim();
    create.mutate(body);
  };

  return (
    <form
      onSubmit={form.handleSubmit(submit)}
      className="rounded-lg border border-[color:var(--color-border)] bg-white p-6"
      noValidate
    >
      <h2 className="text-lg font-semibold">Neuen Raum anlegen</h2>
      <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
        <Field label="Name *" error={form.formState.errors.name?.message}>
          <input
            type="text"
            placeholder="z. B. Klasse 2a"
            className="h-12 w-full rounded-md border border-[color:var(--color-border)] bg-white px-3"
            {...form.register("name")}
          />
        </Field>
        <Field label="Etage (optional)" error={form.formState.errors.etage?.message}>
          <input
            type="text"
            placeholder="z. B. EG, 1. OG, Container 2"
            className="h-12 w-full rounded-md border border-[color:var(--color-border)] bg-white px-3"
            {...form.register("etage")}
          />
        </Field>
        <Field label="Kapazität (optional)" error={form.formState.errors.kapazitaet?.message}>
          <input
            type="number"
            inputMode="numeric"
            min={1}
            max={9999}
            placeholder="z. B. 28"
            className="h-12 w-full rounded-md border border-[color:var(--color-border)] bg-white px-3"
            {...form.register("kapazitaet")}
          />
        </Field>
        <Field label="Notiz (optional)" error={form.formState.errors.notiz?.message}>
          <textarea
            rows={3}
            placeholder="Besonderheiten, z. B. „Klima defekt, Nordseite“"
            className="w-full rounded-md border border-[color:var(--color-border)] bg-white px-3 py-2"
            {...form.register("notiz")}
          />
        </Field>
      </div>
      <div className="mt-4 flex gap-2">
        <button
          type="submit"
          disabled={create.isPending}
          className="h-12 rounded-md bg-[color:var(--color-brand-primary-strong)] px-6 font-semibold text-white disabled:opacity-50"
        >
          {create.isPending ? "Wird angelegt …" : "Anlegen"}
        </button>
        <button
          type="button"
          onClick={onDone}
          className="h-12 rounded-md border border-[color:var(--color-border)] px-6 font-medium hover:bg-[color:var(--color-surface-sunken)]"
        >
          Abbrechen
        </button>
      </div>
    </form>
  );
}

function Field({
  label,
  error,
  children,
}: {
  label: string;
  error?: string;
  children: React.ReactNode;
}) {
  return (
    <label className="flex flex-col gap-1 text-sm">
      <span className="font-medium text-neutral-700">{label}</span>
      {children}
      {error && (
        <span role="alert" className="text-xs text-[color:var(--color-danger)]">
          {error}
        </span>
      )}
    </label>
  );
}
